import React from 'react';
import PropTypes, { any } from 'prop-types';

import Stats from './Stats';
import { Utr, UtrLabel } from '../../text';
import withAuth from '../../hocs/withAuth';
import TeamPlayerV2 from './TeamPlayerV2';
import { isPickleBallSportTypeEvent } from '../../../utils/events';
import { getPickleBallRating } from '../../../utils';
import { gameTypes } from '../../../utils/constants';
import Img from '../../Img';
import { MaximumMedalsToDisplay, sportTypeIds } from '../../../utils/eventConstants';
import { getLabelByTypeId, hasRetiredTag } from '../../../utils/types';

import styles from './teamStandingsCardV3.scss';

function TeamStandingsCardV3(props) {
    const {
        teamPlayers,
        utrType,
        showDecimals,
        auth,
        showRatingNotLoggedIn,
        hideRatings,
        drawMedal,
        stats,
        childDrawStats,
        sportTypeId,
        isRoundRobin = false,
        isAdHoc = false,
        isDoubleElimination = false,
        isFeedInBackDraw = false,
        isFirstMatchBackDraw = false,
        standingsPosition,
        divisionMedal = [],
        outcome = null,
        isIndexDisplayed = false,
        tagId,
        isPttEvent,
        drawTagTypes,
        'data-testid': dataTestId,
    } = props;

    const userLoggedIn = auth?.user;
    const isPickleBallSportType = isPickleBallSportTypeEvent(sportTypeId);
    const isWithdrawnPlayer = !!outcome;
    const shouldDisplayPlayoffColumns = isRoundRobin || isAdHoc;
    const isTeamTagged = isPttEvent && !!tagId && hasRetiredTag(tagId);

    const getTeamStatsSectionClassName = () => {
        const className = (isWithdrawnPlayer || isTeamTagged) ? 'inactive' : '';
        if (isIndexDisplayed) {
            if (shouldDisplayPlayoffColumns) {
                return `col-md-8 ${className}`;
            }

            return `col-md-7 ${className}`;
        }

        if (shouldDisplayPlayoffColumns) {
            return `col-md-9 ${className}`;
        }

        return `col-md-8 ${className}`;
    };

    const renderRank = (drawMedal, standingsPosition, outcome, style, testIdPrefix = null) => {
        if (drawMedal && drawMedal <= MaximumMedalsToDisplay) {
            return (
                <Img
                    src={`/img/medals/${drawMedal}.svg`}
                    style={style}
                    data-testid={dataTestId && testIdPrefix && `${dataTestId}.${testIdPrefix}.medal`}
                />
            );
        }

         if (isTeamTagged) {
            return (<span className={styles.outcome} data-testid={dataTestId && testIdPrefix && `${dataTestId}.${testIdPrefix}.tag-label`}>{getLabelByTypeId(drawTagTypes, tagId)}</span>);
        }

        if (isWithdrawnPlayer) {
            return (
                <span className={styles.outcome} data-testid={dataTestId && testIdPrefix && `${dataTestId}.${testIdPrefix}.outcome`}>
                    {outcome}
                </span>
            );
        }

        return (<span data-testid={dataTestId && testIdPrefix && `${dataTestId}.${testIdPrefix}.position`}>{standingsPosition}</span>);
    };

    return (
        <div className="utr-card player-card player-card-v3 standings-card">
            <div className={`list-view d-none d-md-block`}>
                <div className="d-flex">
                    <div className="flex-grow">
                        <div className="row">
                            {isIndexDisplayed && (
                                <div className="col-1 d-flex justify-content-center br">
                                    <div className="rank" data-testid={dataTestId && `${dataTestId}.desktop.rank`}>
                                        {renderRank(drawMedal, standingsPosition, outcome, { width: '100%' }, 'desktop.rank')}
                                    </div>
                                </div>
                            )}
                            <div className={`col-md-${shouldDisplayPlayoffColumns ? '3' : '4'} ${(isWithdrawnPlayer || isTeamTagged) ? 'inactive' : ''}`}>
                                <div className="w-100">
                                    {teamPlayers.map((player, i) => {
                                        const divMedal = divisionMedal?.find(dm => dm.memberId === player.memberId);
                                        return (
                                            <TeamPlayerV2
                                                key={`team-player-${player.playerId}`}
                                                player={player}
                                                index={i}
                                                divMedal={divMedal}
                                                className={(isWithdrawnPlayer || isTeamTagged) ? 'inactive' : ''}
                                                data-testid={dataTestId && `${dataTestId}.player-${player.playerId}`}
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                            <div className={getTeamStatsSectionClassName()}>
                                <div className="row w-100">
                                    {!hideRatings && (
                                        <div className={`col d-flex jcc aic`}>
                                            {(userLoggedIn || showRatingNotLoggedIn) && !isPickleBallSportType && (
                                                <div className="values">
                                                    {teamPlayers.map((player, i) => {
                                                        const { ratingStatusDoubles } = player;
                                                        return (
                                                            <div key={`team-player-rating-${player.playerId}`} className={`${i === 0 ? 'mb16' : ''}`} data-testid={dataTestId && `${dataTestId}.player-${player.playerId}.doubles-values`}>
                                                                <div
                                                                    className={`value ${utrType === 'sanctioned' && ratingStatusDoubles !== 'Rated' ? 'text-lighter' : ''}`}
                                                                    title={utrType === 'sanctioned' ? ratingStatusDoubles : ''}
                                                                    data-testid={dataTestId && `${dataTestId}.player-${player.playerId}.doubles-utr`}
                                                                >
                                                                    <Utr player={player} force={{ ratingType: 'doubles', utrType, verifiedIcon: true, showDecimals }} sportTypeId={sportTypeId} data-testid={dataTestId && `${dataTestId}.player-${player.playerId}.doubles-utr`} />
                                                                </div>
                                                                <div className="title" data-testid={dataTestId && `${dataTestId}.player-${player.playerId}.doubles-label`}>
                                                                    <UtrLabel player={player} force={{ ratingType: 'doubles', utrType, typeDots: true }} sportTypeId={sportTypeId} data-testid={dataTestId && `${dataTestId}.player-${player.playerId}.doubles-label`} />
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                            {(userLoggedIn || showRatingNotLoggedIn) && isPickleBallSportType && (
                                                <div className="values">
                                                    {teamPlayers.map((player, i) => {
                                                        const playerPkbRating = getPickleBallRating(player, gameTypes.DOUBLES);

                                                        return (
                                                            <div
                                                                key={`team-player-rating-${player.playerId}`}
                                                                className={`${i === 0 ? 'mb16' : ''}`}
                                                            >
                                                                {!!playerPkbRating.value && (
                                                                    <div
                                                                        className={`value ${utrType === 'sanctioned' ? 'text-lighter' : ''}`}
                                                                        title={playerPkbRating.title}
                                                                        data-testid={dataTestId && `${dataTestId}.player-${player.playerId}.pkb-utr`}
                                                                    >
                                                                        {playerPkbRating.value}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {shouldDisplayPlayoffColumns && (
                                        <Stats
                                            stats={childDrawStats}
                                            sportTypeId={sportTypeId}
                                            data-testid={dataTestId && `${dataTestId}.desktop.child-stats`}
                                        />
                                    )}
                                    {stats && (
                                        <Stats
                                            stats={stats}
                                            isDoubleElimination={isDoubleElimination}
                                            isFeedInBackDraw={isFeedInBackDraw}
                                            isFirstMatchBackDraw={isFirstMatchBackDraw}
                                            childDrawStats={childDrawStats}
                                            sportTypeId={sportTypeId}
                                            data-testid={dataTestId && `${dataTestId}.desktop.stats`}
                                        />
                                    )}
                                    {!stats && (
                                        <>
                                            <div className="col" />
                                            <div className="col" />
                                            <div className="col" />
                                            <div className="col" />
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className={`card-view d-block d-md-none ${styles.cardView} ${(isWithdrawnPlayer || isTeamTagged) ? styles.inactive : ''} `}>
                <div className="d-flex flex-row">
                    {isIndexDisplayed && (
                        <div className={`d-flex justify-content-center br`}>
                            <div className={styles.rank} data-testid={dataTestId && `${dataTestId}.mobile.rank`}>
                                {renderRank(drawMedal, standingsPosition, outcome, { width: '30px', height: '30px' }, 'mobile.rank')}
                            </div>
                        </div>
                    )}
                    <div className="d-flex flex-column ml8 pt8">
                        {teamPlayers.map((player, i) => {
                            const divMedal = divisionMedal?.find(dm => dm.memberId === player.memberId);
                            return (
                                <TeamPlayerV2
                                    key={`team-player-${player.playerId}`}
                                    player={player}
                                    index={i}
                                    userLoggedIn={userLoggedIn}
                                    showRatingNotLoggedIn={showRatingNotLoggedIn}
                                    utrType={utrType}
                                    showDecimals={showDecimals}
                                    force={{ cardView: true }}
                                    sportTypeId={sportTypeId}
                                    className={(isWithdrawnPlayer || isTeamTagged) ? 'inactive' : ''}
                                    divMedal={divMedal}
                                    data-testid={dataTestId && `${dataTestId}.mobile.player-${player.playerId}`}
                                />
                            );
                        })}
                    </div>
                </div>
                {stats && (
                    <div className={`d-flex profile-details ${(isWithdrawnPlayer || isTeamTagged) ? 'inactive' : ''} bt mt16 pl0 pr0 pb0`}>
                        <Stats
                            stats={stats}
                            force={{ cardView: true }}
                            sportTypeId={sportTypeId}
                            data-testid={dataTestId && `${dataTestId}.mobile.stats`}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

TeamStandingsCardV3.propTypes = {
    'data-testid': PropTypes.string,
    teamPlayers: PropTypes.objectOf(any).isRequired,
    utrType: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]),
    showDecimals: PropTypes.bool,
    auth: PropTypes.objectOf(any).isRequired,
    showRatingNotLoggedIn: PropTypes.bool,
    hideRatings: PropTypes.bool,
    stats: PropTypes.arrayOf(any),
    childDrawStats: PropTypes.arrayOf(any),
    sportTypeId: PropTypes.number,
    drawMedal: PropTypes.number,
    divisionMedal: PropTypes.number,
    isRoundRobin: PropTypes.bool,
    isAdHoc: PropTypes.bool,
    isDoubleElimination: PropTypes.bool,
    isFeedInBackDraw: PropTypes.bool,
    isFirstMatchBackDraw: PropTypes.bool,
    standingsPosition: PropTypes.bool,
    isIndexDisplayed: PropTypes.bool,
    outcome: PropTypes.string,
    tagId: PropTypes.string,
    isPttEvent: PropTypes.bool,
    drawTagTypes: PropTypes.arrayOf(any).isRequired,
};

TeamStandingsCardV3.defaultProps = {
    'data-testid': null,
    utrType: null,
    showDecimals: false,
    showRatingNotLoggedIn: false,
    hideRatings: false,
    stats: [],
    childDrawStats: [],
    sportTypeId: sportTypeIds.TENNIS,
    drawMedal: null,
    divisionMedal: null,
    isRoundRobin: false,
    isAdHoc: false,
    isDoubleElimination: false,
    isFeedInBackDraw: false,
    isFirstMatchBackDraw: false,
    standingsPosition: false,
    isIndexDisplayed: false,
    outcome: null,
    tagId: null,
    isPttEvent: false,
};

export default withAuth(TeamStandingsCardV3);
